import { refreshToken } from './auth';
import { API_URL } from './config';

export class UnauthorizedError extends Error {
  constructor(message = 'Unauthorized') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

/** Admin istekleri: HttpOnly cookie + credentials (Bearer yok) */
export function getAdminAuthHeaders(extra: Record<string, string> = {}): Record<string, string> {
  return {
    'X-Client': 'web',
    ...extra,
  };
}

async function fetchAdminApi(endpoint: string, options: RequestInit = {}, retried = false) {
  const headers = getAdminAuthHeaders({
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  });

  // Note: Backend Redis cache is used for public API endpoints (news, categories, etc.)
  // Admin API endpoints don't use Redis cache, but frontend useApiCache prevents duplicate calls
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: headers as HeadersInit,
    credentials: 'include',
    // Cache control: no-cache for admin endpoints (data changes frequently)
    // Backend Redis handles caching for public endpoints
    cache: 'no-store',
  });

  if (response.status === 401 && !retried) {
    try {
      await refreshToken();
      return fetchAdminApi(endpoint, options, true);
    } catch {
      throw new UnauthorizedError();
    }
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  if (response.status === 403) {
    // Forbidden - user doesn't have permission
    // Don't show error, just return empty/null data
    // The ProtectedPage component will handle redirect
    const error = await response.json().catch(() => ({ message: 'Yetkiniz bulunmamaktadır' }));
    throw new UnauthorizedError(error.message || 'Yetkiniz bulunmamaktadır');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'İstek başarısız oldu' }));
    const raw = error.message ?? error.error;
    const message = Array.isArray(raw) ? raw.join(', ') : raw;
    throw new Error(message || `HTTP ${response.status}`);
  }

  return response.json();
}

// Articles
export async function getArticles(params?: any) {
  const query = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
  }
  return fetchAdminApi(`/admin/articles?${query.toString()}`);
}

export async function getArticle(id: number) {
  return fetchAdminApi(`/admin/articles/${id}`);
}

export async function createArticle(data: any) {
  return fetchAdminApi('/admin/articles', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateArticle(id: number, data: any) {
  return fetchAdminApi(`/admin/articles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function addArticleLiveUpdate(
  id: number,
  data: { body?: string; title?: string; image?: string; video?: string },
) {
  return fetchAdminApi(`/admin/articles/${id}/live-updates`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateArticleLiveUpdate(
  id: number,
  updateId: number,
  data: { body?: string; title?: string; image?: string; video?: string },
) {
  return fetchAdminApi(`/admin/articles/${id}/live-updates/${updateId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteArticleLiveUpdate(id: number, updateId: number) {
  return fetchAdminApi(`/admin/articles/${id}/live-updates/${updateId}`, {
    method: 'DELETE',
  });
}

export async function getArticleLiveUpdates(id: number) {
  return fetchAdminApi(`/admin/articles/${id}/live-updates`);
}

export async function deleteArticle(id: number) {
  return fetchAdminApi(`/admin/articles/${id}`, {
    method: 'DELETE',
  });
}

export async function publishArticle(id: number) {
  return fetchAdminApi(`/admin/articles/${id}/publish`, {
    method: 'POST',
  });
}

export async function approveArticle(id: number) {
  return fetchAdminApi(`/admin/articles/${id}/approve`, {
    method: 'POST',
  });
}

export async function rejectArticle(id: number, note?: string) {
  return fetchAdminApi(`/admin/articles/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note: note || undefined }),
  });
}

export async function approveAndPublishArticle(id: number) {
  return fetchAdminApi(`/admin/articles/${id}/approve-and-publish`, {
    method: 'POST',
  });
}

export async function getAdminSubmissions(params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  categoryId?: number;
}) {
  const query = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
  }
  const qs = query.toString();
  return fetchAdminApi(`/admin/submissions${qs ? `?${qs}` : ''}`);
}

export async function getAdminSubmission(id: number) {
  return fetchAdminApi(`/admin/submissions/${id}`);
}

export async function updateAdminSubmission(
  id: number,
  body: {
    title?: string;
    summary?: string;
    content?: string;
    categoryId?: number;
    image?: string;
    youtube?: string;
    video?: string;
  },
) {
  return fetchAdminApi(`/admin/submissions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function openAdminSubmissionForEdit(id: number) {
  return fetchAdminApi(`/admin/submissions/${id}/open-for-edit`, {
    method: 'POST',
  });
}

export async function rejectUserSubmission(id: number, note?: string) {
  return fetchAdminApi(`/admin/submissions/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note: note || undefined }),
  });
}

export async function approveAndPublishUserSubmission(id: number) {
  return fetchAdminApi(`/admin/submissions/${id}/approve-and-publish`, {
    method: 'POST',
  });
}

export async function unpublishArticle(id: number) {
  return fetchAdminApi(`/admin/articles/${id}/unpublish`, {
    method: 'POST',
  });
}

export type AdminBulkResult = {
  ok: number;
  fail: number;
  results: Array<{ id: number; ok: boolean; error?: string }>;
};

export async function bulkArticlesAdmin(body: {
  ids: number[];
  action: 'delete' | 'publish' | 'unpublish';
  isType?: number;
}) {
  return fetchAdminApi('/admin/articles/bulk', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<AdminBulkResult>;
}

// Categories
export async function getCategories(search?: string, limit = 200) {
  const query = new URLSearchParams();
  if (search?.trim()) {
    query.set('search', search.trim());
  }
  query.set('limit', String(limit));
  const qs = query.toString();
  return fetchAdminApi(`/admin/categories${qs ? `?${qs}` : ''}`);
}

export async function getCategory(id: number) {
  return fetchAdminApi(`/admin/categories/${id}`);
}

export async function createCategory(data: any) {
  return fetchAdminApi('/admin/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCategory(id: number, data: any) {
  return fetchAdminApi(`/admin/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCategory(id: number) {
  return fetchAdminApi(`/admin/categories/${id}`, {
    method: 'DELETE',
  });
}

export async function reorderCategories(scope: 'header' | 'strip', ids: number[]) {
  return fetchAdminApi('/admin/categories/reorder', {
    method: 'POST',
    body: JSON.stringify({ scope, ids }),
  });
}

// Tags
export async function getTags(params?: { page?: number; limit?: number; search?: string }) {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.search?.trim()) q.set('search', params.search.trim());
  const qs = q.toString();
  return fetchAdminApi(`/admin/tags${qs ? `?${qs}` : ''}`);
}

/** Tüm aktif etiket adları (başlık/AI eşlemesi) */
export async function getTagCatalog(): Promise<{ names: string[] }> {
  return fetchAdminApi('/admin/tags/catalog') as Promise<{ names: string[] }>;
}

export async function getTag(id: number) {
  return fetchAdminApi(`/admin/tags/${id}`);
}

export async function createTag(data: any) {
  return fetchAdminApi('/admin/tags', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTag(id: number, data: any) {
  return fetchAdminApi(`/admin/tags/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteTag(id: number) {
  return fetchAdminApi(`/admin/tags/${id}`, {
    method: 'DELETE',
  });
}

// Media
export async function getMedia(params?: { page?: number; limit?: number; search?: string }) {
  const query = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
  }
  const qs = query.toString();
  return fetchAdminApi(`/admin/media${qs ? `?${qs}` : ''}`);
}

export async function syncMediaCaption(path: string, caption: string) {
  return fetchAdminApi('/admin/media/caption', {
    method: 'PATCH',
    body: JSON.stringify({ path, caption }),
  });
}

export async function syncMediaCaptions(items: Array<{ path: string; caption: string }>) {
  return fetchAdminApi('/admin/media/captions', {
    method: 'PATCH',
    body: JSON.stringify({ items }),
  });
}

export async function fetchVideoMeta(url: string, importThumbnail = true) {
  const query = new URLSearchParams({
    url,
    importThumbnail: importThumbnail ? '1' : '0',
  });
  return fetchAdminApi(`/admin/media/video-meta?${query.toString()}`);
}

export async function importArticleFromUrl(url: string) {
  return fetchAdminApi('/admin/articles/import-from-url', {
    method: 'POST',
    body: JSON.stringify({ url }),
  }) as Promise<{
    title: string;
    titleSeo: string;
    description: string;
    keywords: string;
    content: string;
    image: string | null;
    imageUrl: string | null;
    sourceUrl: string;
    importer?: string;
    articleSection?: string | null;
    categoryId?: number | null;
    categoryTitle?: string | null;
  }>;
}

export type AiRewriteProvider = 'openai' | 'grok';
export type AiRewriteField = 'title' | 'titleSub' | 'titleSeo' | 'description' | 'keywords' | 'content';

/** Bot «default» / boş → site varsayılanı (Ayarlar → Yapay Zeka). */
export function resolveAiRewriteProvider(
  requested?: string | null,
  siteDefault?: string | null,
): AiRewriteProvider {
  const raw = String(requested || '').trim().toLowerCase()
  if (raw === 'grok' || raw === 'openai') return raw
  return String(siteDefault || '').trim().toLowerCase() === 'grok'
    ? 'grok'
    : 'openai'
}
export type AiImageMode = 'generate' | 'edit';
export type AiImagePurpose = 'haber' | 'manset';

export async function getAiImageDefaults() {
  return fetchAdminApi('/admin/articles/ai-defaults') as Promise<{
    defaultProvider: AiRewriteProvider;
  }>;
}

export async function createMansetDesignWithAi(payload: {
  provider?: AiRewriteProvider;
  headline?: string;
  categoryName?: string;
  categorySlug?: string;
  artDirection?: {
    layout?: string;
    font?: string;
    accent?: string;
    mood?: string;
  };
}) {
  return fetchAdminApi('/admin/articles/ai-manset-template', {
    method: 'POST',
    body: JSON.stringify(payload),
  }) as Promise<{
    provider: string;
    model: string;
    name: string;
    json: {
      width: number;
      height: number;
      pages: Array<{
        id: string;
        width: number;
        height: number;
        background: string;
        children: Record<string, unknown>[];
      }>;
    };
  }>;
}

export async function generateArticleImageWithAi(payload: {
  provider?: AiRewriteProvider;
  mode?: AiImageMode;
  purpose?: AiImagePurpose;
  prompt: string;
  headline?: string;
  preset?: string;
  sourceUrl?: string;
  sourceDataUrl?: string;
  sourceImages?: Array<{ url?: string; dataUrl?: string }>;
  removeWatermark?: boolean;
  keepOriginal?: boolean;
}) {
  return fetchAdminApi('/admin/articles/ai-image', {
    method: 'POST',
    body: JSON.stringify(payload),
  }) as Promise<{
    provider: AiRewriteProvider;
    model: string;
    mimeType: string;
    imageBase64: string;
  }>;
}

export async function rewriteArticleWithAi(payload: {
  provider?: AiRewriteProvider;
  fields?: AiRewriteField[];
  title?: string;
  titleSub?: string;
  titleSeo?: string;
  description?: string;
  keywords?: string;
  content?: string;
  extraInstruction?: string;
}) {
  return fetchAdminApi('/admin/articles/ai-rewrite', {
    method: 'POST',
    body: JSON.stringify(payload),
  }) as Promise<{
    provider: AiRewriteProvider;
    model: string;
    title?: string;
    titleSub?: string;
    titleSeo?: string;
    description?: string;
    keywords?: string;
    content?: string;
  }>;
}

export type AiModelsListResult = {
  provider: AiRewriteProvider;
  current: string;
  source: 'api' | 'fallback';
  configured: boolean;
  models: Array<{ id: string; recommended?: boolean; label?: string }>;
  error?: string;
};

/** OpenAI / Grok hesabındaki güncel modeller */
export async function getAiModels(provider?: AiRewriteProvider) {
  const q = provider ? `?provider=${provider}` : '';
  return fetchAdminApi(`/admin/settings/ai-models${q}`) as Promise<AiModelsListResult[]>;
}

export async function importSocialVideo(
  url: string,
  onProgress?: (p: { stage: string; percent: number; message?: string }) => void,
) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/x-ndjson',
    'X-Client': 'web',
  };

  const response = await fetch(`${API_URL}/admin/media/import-social-video`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ url }),
    cache: 'no-store',
  });

  if (response.status === 401 || response.status === 403) {
    throw new UnauthorizedError('Yetkiniz bulunmamaktadır');
  }

  if (!response.ok || !response.body) {
    const error = await response.json().catch(() => ({
      message: 'Video içe aktarılamadı',
    }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: any = null;
  let lastError: string | null = null;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      let event: any;
      try {
        event = JSON.parse(trimmed);
      } catch {
        continue;
      }
      if (event.stage && typeof event.percent === 'number') {
        onProgress?.({
          stage: event.stage,
          percent: event.percent,
          message: event.message,
        });
      }
      if (event.stage === 'done' && event.result) {
        result = event.result;
      }
      if (event.stage === 'error') {
        lastError = event.message || 'İçe aktarma başarısız';
      }
    }
  }

  if (buffer.trim()) {
    try {
      const event = JSON.parse(buffer.trim());
      if (event.stage === 'done' && event.result) result = event.result;
      if (event.stage === 'error') {
        lastError = event.message || 'İçe aktarma başarısız';
      }
      if (event.stage && typeof event.percent === 'number') {
        onProgress?.({
          stage: event.stage,
          percent: event.percent,
          message: event.message,
        });
      }
    } catch {
      /* ignore */
    }
  }

  if (lastError) throw new Error(lastError);
  if (!result?.video) throw new Error('İçe aktarma sonucu alınamadı');
  return result;
}

export async function uploadMedia(formData: FormData) {
  const response = await fetch(`${API_URL}/admin/media/upload`, {
    method: 'POST',
    headers: getAdminAuthHeaders(),
    body: formData,
    credentials: 'include',
  });

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Upload failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function deleteMedia(id: number) {
  return fetchAdminApi(`/admin/media/${id}`, {
    method: 'DELETE',
  });
}

// Users
export async function getUsers(params?: any) {
  const query = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
  }
  return fetchAdminApi(`/admin/users?${query.toString()}`);
}

export async function updateUser(id: number, data: any) {
  return fetchAdminApi(`/admin/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function banUser(userId: number, reason?: string) {
  return fetchAdminApi(`/admin/users/${userId}/ban`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

export async function unbanUser(userId: number) {
  return fetchAdminApi(`/admin/users/${userId}/unban`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function grantUserPremium(userId: number, data: { days?: number; until?: string }) {
  return fetchAdminApi(`/admin/users/${userId}/premium`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function revokeUserPremium(userId: number) {
  return fetchAdminApi(`/admin/users/${userId}/premium`, {
    method: 'DELETE',
  });
}

// Settings
export async function getSettings() {
  return fetchAdminApi('/admin/settings');
}

export async function updateSettings(data: Record<string, string>) {
  return fetchAdminApi('/admin/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Social publish
export type SocialPostListItem = {
  id: number;
  articleId: number;
  platform: string;
  platformLabel?: string;
  status: string;
  permalink?: string | null;
  externalId?: string | null;
  error?: string | null;
  text?: string | null;
  article?: { id: number; title: string; slug: string } | null;
};

export type ArticleSocialPostsResponse = {
  articleId: number;
  masterEnabled: boolean;
  platforms: Array<{
    platform: string;
    label: string;
    auto: boolean;
    defaultSelected: boolean;
    configured?: boolean;
    post: {
      id: number;
      status: string;
      permalink?: string | null;
      externalId?: string | null;
      error?: string | null;
      text?: string | null;
      updatedAt?: string;
    } | null;
  }>;
};

export async function getSocialPosts(params?: {
  articleId?: number;
  platform?: string;
  status?: string;
  page?: number;
  limit?: number;
}) {
  const query = new URLSearchParams();
  if (params?.articleId) query.set('articleId', String(params.articleId));
  if (params?.platform) query.set('platform', params.platform);
  if (params?.status) query.set('status', params.status);
  if (params?.page) query.set('page', String(params.page));
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  return fetchAdminApi(`/admin/social-posts${qs ? `?${qs}` : ''}`) as Promise<{
    data: SocialPostListItem[];
    total: number;
    page: number;
    limit: number;
  }>;
}

export async function getArticleSocialPosts(articleId: number) {
  return fetchAdminApi(
    `/admin/social-posts/by-article/${articleId}`,
  ) as Promise<ArticleSocialPostsResponse>;
}

export async function shareArticleToSocial(body: {
  articleId: number;
  platforms: string[];
  text?: string;
}) {
  return fetchAdminApi('/admin/social-posts', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<ArticleSocialPostsResponse>;
}

export async function retrySocialPost(id: number) {
  return fetchAdminApi(`/admin/social-posts/${id}/retry`, { method: 'POST' });
}

export async function deleteSocialPost(id: number) {
  return fetchAdminApi(`/admin/social-posts/${id}`, { method: 'DELETE' });
}

export async function testSocialConnection(platform: string) {
  return fetchAdminApi(`/admin/social-posts/test/${platform}`, {
    method: 'POST',
  }) as Promise<{ ok: boolean; message: string }>;
}

/** YouTube OAuth: Google consent → refresh_token kaydı */
export async function startYoutubeOauth() {
  return fetchAdminApi('/admin/social-posts/youtube/oauth/start') as Promise<{
    url: string;
    redirectUri: string;
  }>;
}

export async function getYoutubeOauthRedirectUri() {
  return fetchAdminApi(
    '/admin/social-posts/youtube/oauth/redirect-uri',
  ) as Promise<{ redirectUri: string }>;
}

export async function completeYoutubeOauth(body: {
  code?: string;
  state?: string;
  error?: string;
}) {
  return fetchAdminApi('/admin/social-posts/youtube/oauth/complete', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<{ ok: boolean; message: string }>;
}

// Media settings (admin — JWT gerekli)
export async function getMediaSettingsAdmin() {
  return fetchAdminApi('/admin/settings/media');
}

export async function updateMediaSettingsAdmin(data: unknown) {
  return fetchAdminApi('/admin/settings/media', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function testMediaConnectionAdmin(data: unknown) {
  return fetchAdminApi('/admin/settings/media/test-connection?provider=s3', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createMediaBucketAdmin(data: unknown) {
  return fetchAdminApi('/admin/settings/media/create-bucket', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createMediaCloudFrontAdmin(data: unknown) {
  return fetchAdminApi('/admin/settings/media/create-cloudfront', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Dashboard
export async function getDashboardStats(params?: { dateFilter?: string; dateFrom?: string; dateTo?: string }) {
  const query = new URLSearchParams();
  if (params) {
    if (params.dateFilter) query.append('dateFilter', params.dateFilter);
    if (params.dateFrom) query.append('dateFrom', params.dateFrom);
    if (params.dateTo) query.append('dateTo', params.dateTo);
  }
  const queryString = query.toString();
  return fetchAdminApi(`/admin/dashboard${queryString ? `?${queryString}` : ''}`);
}

export type PromoCodeRow = {
  id: number;
  code: string;
  days: number | null;
  untilDate: string | null;
  maxRedemptions: number | null;
  redeemedCount: number;
  active: boolean;
  createdAt: string;
};

export async function getPromoCodes() {
  return fetchAdminApi('/admin/promo-codes') as Promise<{
    codes: PromoCodeRow[];
  }>;
}

export async function createPromoCode(data: {
  code: string;
  days?: number;
  untilDate?: string;
  maxRedemptions?: number;
}) {
  return fetchAdminApi('/admin/promo-codes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function setPromoCodeActive(id: number, active: boolean) {
  return fetchAdminApi(`/admin/promo-codes/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ active }),
  });
}

// Comments
export type AdminCommentStatus = 'pending' | 'visible' | 'hidden' | 'deleted';

export interface AdminComment {
  id: number;
  target_type: string;
  target_id: number;
  content: string;
  status: AdminCommentStatus;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  is_edited?: boolean;
  guest_name?: string | null;
  guest_email?: string | null;
  guest_fingerprint?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  country_code?: string | null;
  country_name?: string | null;
  device_type?: string | null;
  device_label?: string | null;
  client_source?: string | null;
  client_platform?: string | null;
  app_version?: string | null;
  app_build?: string | null;
  report_count?: number;
  user?: {
    id: number;
    name?: string | null;
    surname?: string | null;
    username?: string | null;
    email?: string | null;
  } | null;
  article?: { id: number; title: string; slug?: string | null } | null;
}

export interface AdminCommentsResponse {
  items: AdminComment[];
  total: number;
  page: number;
  totalPages: number;
  statusCounts: Record<'all' | AdminCommentStatus, number>;
}

export async function getPendingComments(params?: { page?: number; limit?: number }) {
  const query = params ? `?page=${params.page || 1}&limit=${params.limit || 50}` : '';
  return fetchAdminApi(`/comments/admin/pending${query}`);
}

export async function getAllComments(params?: {
  page?: number;
  limit?: number;
  status?: AdminCommentStatus;
  search?: string;
  clientSource?: 'web' | 'mobile_app';
  deviceType?: 'mobile' | 'tablet' | 'desktop' | 'bot' | 'unknown';
}): Promise<AdminCommentsResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', String(params.page));
  if (params?.limit) queryParams.append('limit', String(params.limit));
  if (params?.status) queryParams.append('status', params.status);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.clientSource) queryParams.append('clientSource', params.clientSource);
  if (params?.deviceType) queryParams.append('deviceType', params.deviceType);
  const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
  return fetchAdminApi(`/comments/admin/list${query}`);
}

export async function moderateComment(params: {
  commentId: number;
  action: 'hide' | 'unhide' | 'delete' | 'restore' | 'lock' | 'unlock';
  reason?: string;
}) {
  return fetchAdminApi('/comments/admin/moderate', {
    method: 'POST',
    body: JSON.stringify({
      commentId: params.commentId,
      action: params.action,
      reason: params.reason,
    }),
  });
}

export async function bulkModerateComments(body: {
  ids: number[];
  action: 'hide' | 'unhide' | 'delete' | 'restore' | 'purge';
  reason?: string;
}) {
  return fetchAdminApi('/comments/admin/bulk', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<{
    ok: number;
    fail: number;
    results: Array<{ id: number; ok: boolean; error?: string }>;
  }>;
}

export async function emptyCommentTrash(body?: { reason?: string }) {
  return fetchAdminApi('/comments/admin/trash/empty', {
    method: 'POST',
    body: JSON.stringify(body || {}),
  }) as Promise<{ deleted: number }>;
}

export async function updateAdminComment(id: number, data: { content: string; reason?: string }) {
  return fetchAdminApi(`/comments/admin/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Reports
export type CommentReportResolutionAction = 'dismiss' | 'hide' | 'delete';

export interface AdminCommentReport {
  id: number;
  comment_id: number;
  user_id?: number | null;
  guest_fingerprint?: string | null;
  reason: string;
  message?: string | null;
  is_resolved: boolean;
  resolved_at?: string | null;
  resolved_by_id?: number | null;
  created_at: string;
  reporter?: AdminComment['user'];
  user?: AdminComment['user'];
  comment: (AdminComment & {
    author?: AdminComment['user'];
    article?: AdminComment['article'];
  }) | null;
}

export interface AdminCommentReportsResponse {
  items: AdminCommentReport[];
  total: number;
  page: number;
  totalPages: number;
}

export async function getCommentReports(params?: {
  page?: number;
  limit?: number;
  unresolvedOnly?: boolean;
}): Promise<AdminCommentReportsResponse> {
  const query = new URLSearchParams();
  if (params?.page) query.append('page', String(params.page));
  if (params?.limit) query.append('limit', String(params.limit));
  if (params?.unresolvedOnly !== undefined) {
    query.append('unresolvedOnly', params.unresolvedOnly ? 'true' : 'false');
  }
  const qs = query.toString();
  return fetchAdminApi(`/comments/admin/reports${qs ? `?${qs}` : ''}`);
}

export async function resolveReport(
  id: number,
  data: { action: CommentReportResolutionAction; reason?: string },
) {
  return fetchAdminApi(`/comments/admin/reports/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Audit Logs
export async function getAuditLogs(params?: any) {
  const query = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
  }
  return fetchAdminApi(`/admin/audit-logs?${query.toString()}`);
}

export async function getAuditLogFilters() {
  return fetchAdminApi('/admin/audit-logs/filters');
}

// Content Audit (İçerik Denetimi)
export async function getContentAudit(params?: {
  page?: number;
  limit?: number;
  status?: string;
  issue?: string;
  search?: string;
  minPlainChars?: number;
}) {
  const query = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
  }
  return fetchAdminApi(`/admin/content-audit?${query.toString()}`);
}

export async function getContentAuditWhitelist() {
  return fetchAdminApi('/admin/content-audit/whitelist');
}

export async function updateContentAuditWhitelist(raw: string) {
  return fetchAdminApi('/admin/content-audit/whitelist', {
    method: 'PUT',
    body: JSON.stringify({ raw }),
  });
}

// Cache
export async function getCacheStatus() {
  return fetchAdminApi('/admin/cache/status');
}

export async function enableCache() {
  return fetchAdminApi('/admin/cache/enable', {
    method: 'POST',
  });
}

export async function disableCache() {
  return fetchAdminApi('/admin/cache/disable', {
    method: 'POST',
  });
}

export async function updateCacheTtl(ttl: any) {
  return fetchAdminApi('/admin/cache/ttl', {
    method: 'PUT',
    body: JSON.stringify({ ttl }),
  });
}

export async function clearAllCache() {
  return fetchAdminApi('/admin/cache/clear', {
    method: 'POST',
  });
}

export async function purgeCloudflareCache(body: {
  everything?: boolean;
  files?: string[];
  prefixes?: string[];
  tags?: string[];
  hosts?: string[];
  betaDefaults?: boolean;
}) {
  return fetchAdminApi('/admin/cache/cloudflare/purge', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// GA4 Analytics (Data API — no DB writes)
export type Ga4Range = 'today' | 'yesterday' | '7d' | '28d' | '90d' | '1y';

export async function getGa4AnalyticsBundle(range: Ga4Range = '28d') {
  return fetchAdminApi(`/admin/analytics/ga4/bundle?range=${range}`) as Promise<{
    status: {
      configured: boolean;
      measurementId: string | null;
      propertyId: string | null;
      hasClientEmail: boolean;
      hasPrivateKey: boolean;
      note: string;
    };
    overview: {
      configured: boolean;
      range: Ga4Range;
      sessions?: number;
      users?: number;
      pageViews?: number;
      engagedSessions?: number;
    };
    today?: {
      configured: boolean;
      sessions: number;
      users: number;
      pageViews: number;
    };
    yesterday?: {
      configured: boolean;
      sessions: number;
      users: number;
      pageViews: number;
    };
    daily: {
      configured: boolean;
      range: Ga4Range;
      rows: Array<{ date: string; sessions: number; pageViews: number; users: number }>;
      error?: string;
    };
    topArticles: {
      configured: boolean;
      range: Ga4Range;
      source: 'article_view' | 'page_path' | null;
      rows: Array<{ contentId: string; articleId: number | null; views: number }>;
      items?: Array<{
        articleId: number;
        title: string | null;
        views: number;
        path: string | null;
        editPath: string;
      }>;
    };
    topPages: {
      configured: boolean;
      range: Ga4Range;
      rows: Array<{
        path: string;
        title: string;
        pageViews: number;
        sessions: number;
        articleId: number | null;
      }>;
    };
    sources: {
      configured: boolean;
      range: Ga4Range;
      rows: Array<{ source: string; sessions: number; users: number }>;
    };
    shares: {
      configured: boolean;
      range: Ga4Range;
      rows: Array<{
        contentId: string;
        method: string;
        count: number;
        articleId: number | null;
      }>;
      error?: string;
    };
    topShared?: {
      configured: boolean;
      range: Ga4Range;
      source: 'ga4' | 'db';
      items: Array<{
        articleId: number;
        title: string | null;
        views: number;
        path: string | null;
        editPath: string;
      }>;
    };
  }>;
}

export async function getGa4AnalyticsStatus() {
  return fetchAdminApi('/admin/analytics/ga4/status');
}

export type Ga4Realtime = {
  configured: boolean;
  activeUsers: number;
  pageViews: number;
  screens: Array<{
    name: string;
    users: number;
    views: number;
  }>;
  countries: Array<{ name: string; users: number }>;
  devices: Array<{ name: string; users: number }>;
  window?: string;
  error?: string;
  fetchedAt?: string;
};

export async function getGa4Realtime(limit = 12) {
  return fetchAdminApi(
    `/admin/analytics/ga4/realtime?limit=${limit}`,
  ) as Promise<Ga4Realtime>;
}

export async function getGa4TopShared(
  range: 'today' | '7d' | '28d' | '1y' = '7d',
  limit = 10,
) {
  return fetchAdminApi(
    `/admin/analytics/ga4/top-shared?range=${range}&limit=${limit}`,
  ) as Promise<{
    range: string;
    source: string;
    periodExact?: boolean;
    items: Array<{
      articleId: number;
      title: string | null;
      views: number;
      path?: string | null;
      editPath?: string;
    }>;
  }>;
}

export type GscInspectRow = {
  id: number;
  title: string;
  url: string;
  path: string;
  createdAt: string;
  coverageState: string | null;
  verdict: string | null;
  lastCrawlTime: string | null;
  googleCanonical: string | null;
  pageFetchState: string | null;
  robotsTxtState: string | null;
  indexingState: string | null;
  indexed: boolean;
  error?: string;
};

export async function getGscStatus() {
  return fetchAdminApi('/admin/seo/gsc/status') as Promise<{
    configured: boolean;
    siteUrl: string | null;
    clientEmail: string | null;
    missing: string[];
    error?: string;
    note: string;
  }>;
}

export type GscScanJob = {
  id: string;
  status: 'running' | 'done' | 'error';
  hours: number;
  limit: number;
  siteUrl: string | null;
  total: number;
  done: number;
  indexed: number;
  notIndexed: number;
  rows: GscInspectRow[];
  error?: string;
};

export async function startGscScan(hours = 48, limit = 40) {
  return fetchAdminApi(
    `/admin/seo/gsc/unindexed/start?hours=${hours}&limit=${limit}`,
    { method: 'POST' },
  ) as Promise<GscScanJob>;
}

export async function getGscScanJob(id: string) {
  return fetchAdminApi(`/admin/seo/gsc/unindexed/jobs/${id}`) as Promise<GscScanJob>;
}

export async function inspectGscUrl(url: string) {
  return fetchAdminApi('/admin/seo/gsc/inspect', {
    method: 'POST',
    body: JSON.stringify({ url }),
  }) as Promise<{
    coverageState: string | null;
    verdict: string | null;
    lastCrawlTime: string | null;
    googleCanonical: string | null;
    pageFetchState: string | null;
    robotsTxtState: string | null;
    indexingState: string | null;
    indexed: boolean;
  }>;
}

export async function getGa4DashboardSnapshot() {
  return fetchAdminApi('/admin/analytics/ga4/dashboard-snapshot') as Promise<{
    status: {
      configured: boolean;
      measurementId: string | null;
      propertyId: string | null;
      note: string;
      apiError?: string | null;
    };
    realtime: {
      configured: boolean;
      activeUsers: number;
      pageViews: number;
      screens: Array<{
        name: string;
        users: number;
        views: number;
      }>;
      countries: Array<{ name: string; users: number }>;
      devices: Array<{ name: string; users: number }>;
      window?: string;
      error?: string;
      fetchedAt?: string;
    } | null;
    today: {
      configured: boolean;
      sessions: number;
      users: number;
      pageViews: number;
    } | null;
    yesterday?: {
      configured: boolean;
      sessions: number;
      users: number;
      pageViews: number;
    } | null;
    topArticlesToday: Array<{
      articleId: number;
      title: string | null;
      views: number;
      path?: string | null;
      editPath?: string;
    }>;
    topArticlesRange?: string;
    topArticlesSource?: string | null;
    topShared?: Array<{
      articleId: number;
      title: string | null;
      views: number;
      path?: string | null;
      editPath?: string;
    }>;
    topSharedSource?: string | null;
  }>;
}

// Menu Items
export async function getMenuItems() {
  return fetchAdminApi('/admin/menu/items');
}

// Roles (CASL-based)
export async function getRoles(params?: { page?: number; limit?: number; search?: string }) {
  const query = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.append(key, String(value));
      }
    });
  }
  return fetchAdminApi(`/admin/roles?${query.toString()}`);
}

export async function getRole(id: number) {
  return fetchAdminApi(`/admin/roles/${id}`);
}

export async function createRole(data: { name: string; description?: string }) {
  return fetchAdminApi('/admin/roles', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRole(id: number, data: { name?: string; description?: string }) {
  return fetchAdminApi(`/admin/roles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRole(id: number) {
  return fetchAdminApi(`/admin/roles/${id}`, {
    method: 'DELETE',
  });
}

export async function getRoleUsers(id: number) {
  return fetchAdminApi(`/admin/roles/${id}/users`);
}

export async function getRolePolicy(id: number) {
  return fetchAdminApi(`/admin/roles/${id}/policy`);
}

export async function updateRolePolicy(id: number, policy: any) {
  return fetchAdminApi(`/admin/roles/${id}/policy`, {
    method: 'PUT',
    body: JSON.stringify({ policy }), // Backend expects { policy: ... }
  });
}

export async function assignUsersToRole(roleId: number, userIds: number[]) {
  return fetchAdminApi(`/admin/roles/${roleId}/users`, {
    method: 'PUT',
    body: JSON.stringify({ userIds }),
  });
}

// Permissions (CASL-based)
export async function getPermissions() {
  return fetchAdminApi('/admin/permissions');
}

export async function getPermissionRegistry() {
  return fetchAdminApi('/admin/permissions/registry');
}

export async function getRolePermissions(roleId: number) {
  return fetchAdminApi(`/admin/permissions/role/${roleId}`);
}

export async function updateRolePermissions(roleId: number, permissionIds: number[]) {
  return fetchAdminApi(`/admin/permissions/role/${roleId}`, {
    method: 'PUT',
    body: JSON.stringify({ permissionIds }),
  });
}

export async function getPermissionRoles(permissionId: number) {
  return fetchAdminApi(`/admin/permissions/permission/${permissionId}/roles`);
}

export async function getPermission(permissionId: number) {
  return fetchAdminApi(`/admin/permissions/permission/${permissionId}`);
}

export async function createPermission(data: {
  name: string;
  description?: string;
  group?: string;
  action?: string;
  subject?: string;
  inverted?: boolean;
  conditionsJson?: any;
  fieldsJson?: string[];
}) {
  return fetchAdminApi('/admin/permissions/permission', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePermission(
  permissionId: number,
  data: {
    name?: string;
    description?: string;
    group?: string;
    action?: string;
    subject?: string;
    inverted?: boolean;
    conditionsJson?: any;
    fieldsJson?: string[];
  },
) {
  return fetchAdminApi(`/admin/permissions/permission/${permissionId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePermission(permissionId: number) {
  return fetchAdminApi(`/admin/permissions/permission/${permissionId}/delete`, {
    method: 'POST',
  });
}

// User Role Assignment
export async function assignRoleToUser(userId: number, roleId: number) {
  return fetchAdminApi(`/admin/users/${userId}/roles`, {
    method: 'POST',
    body: JSON.stringify({ roleId }),
  });
}

export async function removeRoleFromUser(userId: number, roleId: number) {
  return fetchAdminApi(`/admin/users/${userId}/roles/${roleId}`, {
    method: 'DELETE',
  });
}

export async function replaceUserRole(userId: number, roleId: number | null) {
  return fetchAdminApi(`/admin/users/${userId}/roles`, {
    method: 'PUT',
    body: JSON.stringify({ roleId }),
  });
}

/** Atomik rol senkronu — CASL user_roles + legacy users.role birlikte güncellenir */
export async function syncUserRoles(userId: number, roleIds: number[]) {
  return fetchAdminApi(`/admin/users/${userId}/roles`, {
    method: 'PUT',
    body: JSON.stringify({ roleIds }),
  });
}

export async function getUserRoles(userId: number) {
  return fetchAdminApi(`/admin/users/${userId}/roles`);
}

export async function getPagePermissions() {
  return fetchAdminApi('/admin/menu/permissions');
}

// Push notifications (mobile)
export async function getPushStats() {
  return fetchAdminApi('/admin/push/stats');
}

export type PushTarget = 'mobile' | 'web';

export async function sendPushNotification(data: {
  title: string;
  body: string;
  newsId?: number;
  imageUrl?: string;
  linkUrl?: string;
  targets?: PushTarget[];
  locales?: string[];
  countries?: string[];
  sendToAll?: boolean;
}) {
  return fetchAdminApi('/admin/push/send', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Yayınlanmış haberi FCM ile seçili kitleye gönder */
export async function sendNewsPushNotification(
  newsId: number,
  options?: {
    imageUrl?: string;
    linkUrl?: string;
    targets?: PushTarget[];
    locales?: string[];
    countries?: string[];
    sendToAll?: boolean;
  },
) {
  return fetchAdminApi(`/admin/push/news/${newsId}`, {
    method: 'POST',
    body: JSON.stringify({
      imageUrl: options?.imageUrl?.trim() || undefined,
      linkUrl: options?.linkUrl?.trim() || undefined,
      sendToAll: options?.sendToAll === true,
      targets: options?.sendToAll === true ? undefined : options?.targets?.length ? options.targets : ['mobile', 'web'],
      locales: options?.sendToAll === true || !options?.locales?.length ? undefined : options.locales,
      countries: options?.sendToAll === true || !options?.countries?.length ? undefined : options.countries,
    }),
  });
}

// --- Shorts Bot ---

export type ShortsBotSettings = {
  enabled: boolean;
  intervalMinutes: number;
  hashtags: string[];
  channelWhitelist: string[];
  downloadToServer: boolean;
  publishOnApprove: boolean;
  lastRunAt?: string | null;
};

export type ShortsBotCategoryOption = {
  id: number;
  title: string;
  slug: string;
};

export type ShortsBotSearchItem = {
  youtubeId: string;
  title: string;
  channelName?: string;
  channelId?: string;
  channelUrl?: string;
  channelAvatarUrl?: string;
  thumbnailUrl: string;
  durationSec?: number;
  pageUrl: string;
  onSite: boolean;
  inBot: boolean;
  botItemId: number | null;
  blocked?: boolean;
};

export type ShortsBotInboxItem = {
  id: number;
  youtubeId: string;
  title: string;
  channelName: string | null;
  channelId: string | null;
  channelUrl: string | null;
  channelAvatarUrl: string | null;
  thumbnailUrl: string | null;
  sourceHashtag: string | null;
  durationSec: number | null;
  pageUrl: string | null;
  categoryId: number | null;
  categoryTitle: string | null;
  status: string;
  downloadToServer: boolean;
  newsId: number | null;
  youtubeShortId: number | null;
  source: string;
  createdAt: string;
};

export type ShortsBotSearchResponse = {
  tag: string;
  searchTag?: string;
  categories: ShortsBotCategoryOption[];
  suggestedCategoryId: number | null;
  suggestedCategoryTitle: string | null;
  items: ShortsBotSearchItem[];
};

export async function getShortsBotCategories() {
  return fetchAdminApi('/admin/shorts-bot/categories') as Promise<ShortsBotCategoryOption[]>;
}

export async function getShortsBotSettings() {
  return fetchAdminApi('/admin/shorts-bot/settings') as Promise<ShortsBotSettings>;
}

export async function updateShortsBotSettings(body: Partial<ShortsBotSettings>) {
  return fetchAdminApi('/admin/shorts-bot/settings', {
    method: 'PUT',
    body: JSON.stringify(body),
  }) as Promise<ShortsBotSettings>;
}

export async function searchShortsBot(tag: string, limit = 30) {
  const q = new URLSearchParams({ tag, limit: String(limit) });
  return fetchAdminApi(`/admin/shorts-bot/search?${q}`) as Promise<ShortsBotSearchResponse>;
}

export type ShortsBotItemPayload = {
  youtubeId: string;
  title?: string;
  channelName?: string;
  thumbnailUrl?: string;
  durationSec?: number;
  pageUrl?: string;
  categoryId?: number | null;
};

export async function getShortsBotInbox(page = 1, limit = 30) {
  const q = new URLSearchParams({ page: String(page), limit: String(limit) });
  return fetchAdminApi(`/admin/shorts-bot/inbox?${q}`) as Promise<{
    items: ShortsBotInboxItem[];
    page: number;
    total: number;
    totalPages: number;
  }>;
}

export async function saveShortsBotInbox(body: {
  items: ShortsBotItemPayload[];
  sourceHashtag?: string;
  downloadToServer?: boolean;
}) {
  return fetchAdminApi('/admin/shorts-bot/inbox', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<{
    ok: number;
    fail: number;
    results: Array<{
      youtubeId: string;
      ok: boolean;
      inboxId?: number;
      error?: string;
    }>;
  }>;
}

export async function updateShortsBotInbox(id: number, body: { title?: string; categoryId?: number | null }) {
  return fetchAdminApi(`/admin/shorts-bot/inbox/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }) as Promise<ShortsBotInboxItem>;
}

export async function publishShortsBotInbox(
  id: number,
  body: {
    title?: string;
    categoryId?: number | null;
    downloadToServer?: boolean;
    publish?: boolean;
  },
) {
  return fetchAdminApi(`/admin/shorts-bot/inbox/${id}/publish`, {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<{ youtubeShortId: number; title: string; inboxId: number }>;
}

export async function rejectShortsBotInbox(id: number) {
  return fetchAdminApi(`/admin/shorts-bot/inbox/${id}`, {
    method: 'DELETE',
  }) as Promise<{
    ok: boolean;
  }>;
}

export async function clearShortsBotInbox() {
  return fetchAdminApi('/admin/shorts-bot/inbox/clear', {
    method: 'POST',
  }) as Promise<{
    cleared: number;
  }>;
}

export async function publishShortsBotItems(body: {
  items: ShortsBotItemPayload[];
  sourceHashtag?: string;
  downloadToServer?: boolean;
  publish?: boolean;
}) {
  return fetchAdminApi('/admin/shorts-bot/publish', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<{
    ok: number;
    fail: number;
    results: Array<{
      youtubeId: string;
      ok: boolean;
      youtubeShortId?: number;
      title?: string;
      error?: string;
    }>;
  }>;
}

export type YoutubeShortAdminItem = {
  id: number;
  youtubeId: string;
  title: string;
  description: string | null;
  image: string | null;
  video: string | null;
  playerType: number;
  channelName: string | null;
  channelId: string | null;
  channelUrl: string | null;
  channelAvatarUrl: string | null;
  sourceHashtag: string | null;
  durationSec: number | null;
  viewCount: number;
  likeCount: number;
  shareCount: number;
  status: string;
  source: string;
  createdAt: string;
  updatedAt: string;
};

export async function getYoutubeShortsAdminList(params?: {
  page?: number;
  limit?: number;
  status?: string;
  q?: string;
  channel?: string;
}) {
  const q = new URLSearchParams();
  if (params?.page) q.set('page', String(params.page));
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.status) q.set('status', params.status);
  if (params?.q) q.set('q', params.q);
  if (params?.channel) q.set('channel', params.channel);
  return fetchAdminApi(`/admin/youtube-shorts?${q}`) as Promise<{
    items: YoutubeShortAdminItem[];
    page: number;
    total: number;
    totalPages: number;
  }>;
}

export async function getYoutubeShortAdmin(id: number) {
  return fetchAdminApi(`/admin/youtube-shorts/${id}`) as Promise<YoutubeShortAdminItem>;
}

export async function updateYoutubeShortAdmin(
  id: number,
  body: {
    title?: string;
    description?: string;
    status?: string;
  },
) {
  return fetchAdminApi(`/admin/youtube-shorts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }) as Promise<YoutubeShortAdminItem>;
}

export async function deleteYoutubeShortAdmin(id: number) {
  return fetchAdminApi(`/admin/youtube-shorts/${id}`, {
    method: 'DELETE',
  }) as Promise<{ ok: boolean }>;
}

export async function bulkYoutubeShortsAdmin(body: { ids: number[]; action: 'delete' | 'publish' | 'unpublish' }) {
  return fetchAdminApi('/admin/youtube-shorts/bulk', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<AdminBulkResult>;
}

/** @deprecated publishShortsBotItems kullanın */
export async function importShortsBotItems(body: {
  items: ShortsBotItemPayload[];
  sourceHashtag?: string;
  downloadToServer?: boolean;
}) {
  return publishShortsBotItems(body);
}

export async function runShortsBotNow() {
  return fetchAdminApi('/admin/shorts-bot/run-now', {
    method: 'POST',
  }) as Promise<{
    skipped?: boolean;
    reason?: string;
    discovered?: number;
    savedToInbox?: number;
    published?: number;
    error?: string;
  }>;
}

export type ShortsBotBlock = {
  id: number;
  blockType: 'video' | 'channel';
  targetId: string;
  label: string | null;
  createdAt: string;
};

export async function getShortsBotBlocks() {
  return fetchAdminApi('/admin/shorts-bot/blocks') as Promise<ShortsBotBlock[]>;
}

export async function addShortsBotBlock(body: {
  blockType: 'video' | 'channel';
  targetId: string;
  label?: string | null;
}) {
  return fetchAdminApi('/admin/shorts-bot/blocks', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<ShortsBotBlock>;
}

export async function removeShortsBotBlock(id: number) {
  return fetchAdminApi(`/admin/shorts-bot/blocks/${id}`, {
    method: 'DELETE',
  }) as Promise<{
    ok: boolean;
  }>;
}

// --- News Bot ---

export type NewsBotSettings = {
  enabled: boolean
  dryRun: boolean
  intervalMinutes: number
  autoAddIntervalMinutes: number
  defaultAiProvider: string
  defaultAutoMode: string
  autoAiRewrite: boolean
  autoPublishEnabled: boolean
  autoPublishMinScore: number
  maxItemsPerRunGlobal: number
  maxAiRewritesPerDay: number
  maxAutoPublishPerDay: number
  /** Bugün bot’tan yayına alınan */
  autoPublishedToday?: number
  /** Günlük otomatik yayın kotası dolu → aktarım durdu */
  autoPublishQuotaFull?: boolean
  /** Kapak engeli: URL substring listesi */
  blockedImageUrlPatterns?: string[]
  dedupeWindowHours: number
  fetchLookbackHours: number
  sameStoryAiMinConfidence: number
  defaultScheduleDelayMinutes: number
  systemEditorId: number | null
  /** Genel bot editör havuzu */
  editorPoolIds: number[]
  /** Kategori id → editör id[] */
  editorPoolByCategory: Record<string, number[]>
  lastRunAt: string | null
  updatedAt: string
}

export type NewsBotSource = {
  id: number
  name: string
  baseUrl: string | null
  rssUrl: string | null
  listUrl: string | null
  importerKey: string
  enabled: boolean
  trusted: boolean
  priority: number
  maxPerRun: number
  defaultCategoryId: number | null
  aiProvider: string | null
  /** rss: RSS enclosure · schema: schema.org / og:image */
  coverImageSource?: 'rss' | 'schema'
  autoMode: string
  consecutiveFailCount: number
  pausedUntil: string | null
  lastRunAt: string | null
  lastSuccessAt: string | null
  lastError: string | null
  defaultCategory?: { id: number; title: string; slug: string } | null
  _count?: { inbox: number }
}

export type NewsBotInboxItem = {
  id: number
  sourceId: number
  sourceUrl: string
  canonicalUrl?: string | null
  status: string
  rawTitle: string | null
  rawDescription: string | null
  rawContent?: string | null
  rawImageUrl?: string | null
  localImagePath?: string | null
  categoryId?: number | null
  error: string | null
  newsId?: number | null
  scheduledAt?: string | null
  fetchedAt?: string | null
  createdAt: string
  updatedAt?: string
  source?: {
    id: number
    name: string
    enabled: boolean
    autoMode?: string
    rssUrl?: string | null
    defaultCategoryId?: number | null
    aiProvider?: string | null
  }
  category?: { id: number; title: string; slug: string } | null
  news?: {
    id: number
    title: string
    slug: string
    status: string
    category?: { id: number; title: string; slug: string } | null
  } | null
}

export type NewsBotRunLog = {
  id: number
  trigger: string
  startedAt: string
  finishedAt: string | null
  discovered: number
  fetched: number
  duplicate: number
  failed: number
  dryRun: boolean
  errorSummary: string | null
}

export async function getNewsBotSettings() {
  return fetchAdminApi('/admin/news-bot/settings') as Promise<NewsBotSettings>
}

export async function updateNewsBotSettings(body: Partial<NewsBotSettings>) {
  return fetchAdminApi('/admin/news-bot/settings', {
    method: 'PUT',
    body: JSON.stringify(body),
  }) as Promise<NewsBotSettings>
}

export async function listNewsBotSources() {
  return fetchAdminApi('/admin/news-bot/sources') as Promise<NewsBotSource[]>
}

export async function createNewsBotSource(body: Partial<NewsBotSource> & { name: string }) {
  return fetchAdminApi('/admin/news-bot/sources', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<NewsBotSource>
}

/** Bilinen TR haber RSS presetlerini ekle (NTV, Hürriyet, TRT, AA vb.) */
export async function seedNewsBotPresetSources() {
  return fetchAdminApi('/admin/news-bot/sources/seed-presets', {
    method: 'POST',
  }) as Promise<{ created: number; updated: number; total: number; presets: number }>
}

export async function updateNewsBotSource(id: number, body: Partial<NewsBotSource>) {
  return fetchAdminApi(`/admin/news-bot/sources/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }) as Promise<NewsBotSource>
}

export async function setNewsBotSourceEnabled(id: number, enabled: boolean) {
  return fetchAdminApi(`/admin/news-bot/sources/${id}/enabled`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  }) as Promise<NewsBotSource>
}

export async function deleteNewsBotSource(id: number) {
  return fetchAdminApi(`/admin/news-bot/sources/${id}`, {
    method: 'DELETE',
  }) as Promise<{ ok: boolean }>
}

export async function previewNewsBotRss(rssUrl: string, limit = 8) {
  return fetchAdminApi('/admin/news-bot/rss/preview', {
    method: 'POST',
    body: JSON.stringify({ rssUrl, limit }),
  }) as Promise<{
    count: number
    items: Array<{
      title: string
      link: string
      publishedAt: string | null
      imageUrl: string | null
    }>
  }>
}

export async function listNewsBotInbox(params?: {
  page?: number
  limit?: number
  status?: string
  sourceId?: number
  categoryId?: number
}) {
  const q = new URLSearchParams()
  if (params?.page) q.set('page', String(params.page))
  if (params?.limit) q.set('limit', String(params.limit))
  if (params?.status) q.set('status', params.status)
  if (params?.sourceId) q.set('sourceId', String(params.sourceId))
  if (params?.categoryId) q.set('categoryId', String(params.categoryId))
  const suffix = q.toString() ? `?${q}` : ''
  return fetchAdminApi(`/admin/news-bot/inbox${suffix}`) as Promise<{
    total: number
    page: number
    limit: number
    items: NewsBotInboxItem[]
  }>
}

export async function getNewsBotInboxItem(id: number) {
  return fetchAdminApi(`/admin/news-bot/inbox/${id}`) as Promise<NewsBotInboxItem>
}

export async function listNewsBotRuns(limit = 20) {
  return fetchAdminApi(`/admin/news-bot/runs?limit=${limit}`) as Promise<NewsBotRunLog[]>
}

export async function runNewsBotNow() {
  return fetchAdminApi('/admin/news-bot/run-now', {
    method: 'POST',
  }) as Promise<{
    ok: boolean
    message?: string
    dryRun?: boolean
    discovered?: number
    duplicate?: number
    fetched?: number
    transferred?: number
    failed?: number
    runId?: number
    errors?: string[]
  }>
}

export async function scrapeNewsBotInboxItem(id: number) {
  return fetchAdminApi(`/admin/news-bot/inbox/${id}/scrape`, {
    method: 'POST',
  })
}

export async function transferNewsBotInboxItem(
  id: number,
  body?: { scheduledAt?: string | null },
) {
  return fetchAdminApi(`/admin/news-bot/inbox/${id}/transfer`, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  }) as Promise<{
    ok: boolean
    newsId: number
    title: string
    scheduledAt: string | null
    status: string
    editPath: string
    aiApplied?: boolean
    aiError?: string | null
    hasImage?: boolean
  }>
}

export async function rejectNewsBotInboxItem(id: number, reason?: string) {
  return fetchAdminApi(`/admin/news-bot/inbox/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export type NewsBotTrendItem = {
  query: string
  traffic: string | null
  trafficValue: number
  pubDate: string | null
  picture: string | null
  pictureSource: string | null
  googleNews: Array<{
    title: string
    url: string
    source: string
    picture: string | null
  }>
  relatedNews: Array<{
    id: number
    title: string
    slug: string
    status: number
    createdAt: string
    isBot: boolean
    category: { id: number; title: string; slug: string } | null
  }>
  relatedInbox: Array<{
    id: number
    rawTitle: string | null
    status: string
    sourceUrl: string
    newsId: number | null
    createdAt: string
  }>
  coverageGap: boolean
}

export type NewsBotTrendsResponse = {
  geo: string
  source: string
  fetchedAt: string
  cached: boolean
  lookbackHours: number
  count: number
  gapCount: number
  items: NewsBotTrendItem[]
}

export async function listNewsBotTrends(params?: {
  refresh?: boolean
  hours?: number
}) {
  const q = new URLSearchParams()
  if (params?.refresh) q.set('refresh', '1')
  if (params?.hours) q.set('hours', String(params.hours))
  const suffix = q.toString() ? `?${q}` : ''
  return fetchAdminApi(`/admin/news-bot/trends${suffix}`) as Promise<NewsBotTrendsResponse>
}
